import { Extension } from '@tiptap/core'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { type EditorState, Plugin, PluginKey, type Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

/**
 * Zoeken binnen één rich-text-veld (R-166, G047).
 *
 * **Alleen markeren, nooit het document veranderen.** De treffers zijn decoraties: een laag over de
 * weergave die niet in het document staat. Een mark zoals `highlight` zou wel in het document komen,
 * dus via de samenwerklaag bij elke medeschrijver en in de revisie; dat is precies wat zoeken niet
 * mag doen. De stand van het zoeken gaat mee als meta op een transactie zonder stappen, langs
 * dezelfde weg als een verplaatste cursor, en levert dus geen Yjs-wijziging op (de test daarvoor
 * staat in `richtextFind.test.tsx`).
 *
 * **Per editor en niet in `RICH_TEXT_EXTENSIONS`.** Die lijst is ook het schema waarmee een fragment
 * buiten de editor om wordt gelezen (valkuil 2 en 3); deze plugin voegt geen knoop en geen mark toe
 * en hoort daar dus niet. Hij staat in `Richtext.tsx` naast `Collaboration`, om dezelfde reden als die.
 *
 * **Hoofdletters en accenten tellen niet mee**, teken voor teken (`foldForSearch`): "ideeen" vindt
 * "ideeën". Een treffer blijft binnen één alinea, loopt wel door over een wissel van opmaak, en een
 * formule of bronvermelding in de tekst telt als één teken dat nergens op lijkt, zodat de posities
 * erna kloppen.
 */

export interface FindMatch {
  readonly from: number
  readonly to: number
}

export interface FindState {
  readonly query: string
  /** Welke treffer de huidige is, of -1 zonder treffers. */
  readonly active: number
  readonly matches: readonly FindMatch[]
  readonly decorations: DecorationSet
}

/** Wat een aanroeper aan de stand verandert, als meta op een transactie. */
export interface FindChange {
  readonly query?: string
  readonly active?: number
}

export const FIND_KEY = new PluginKey<FindState>('zoeken')

/** Een teken dat geen zoekterm raakt: de plek van een formule, bronvermelding of figuur. */
const ATOM = '￼'

/**
 * Eén teken vereenvoudigd voor de vergelijking: zonder accent en in kleine letters.
 *
 * Teken voor teken en met behoud van de lengte, want de positie van elk teken is de positie in het
 * document. Waar vereenvoudigen de lengte zou veranderen ("ß", "İ") blijft het teken zoals het is.
 */
function foldChar(char: string): string {
  const plain = char.normalize('NFD').replace(/\p{M}/gu, '')
  const lower = (plain.length === 1 ? plain : char).toLowerCase()
  if (lower.length === 1) return lower
  const fallback = char.toLowerCase()
  return fallback.length === 1 ? fallback : char
}

/** Een tekst vereenvoudigd voor de vergelijking, met dezelfde lengte als de tekst zelf. */
export function foldForSearch(text: string): string {
  let out = ''
  for (let index = 0; index < text.length; index += 1) {
    const unit = text.charCodeAt(index)
    // Een helft van een surrogaatpaar (een emoji) laten we staan: er valt niets aan te vereenvoudigen.
    out += unit >= 0xd800 && unit <= 0xdfff ? text[index] : foldChar(text[index] ?? '')
  }
  return out
}

/** Elke treffer van de zoekterm in het document, in volgorde en zonder overlap. */
export function findMatches(doc: ProseMirrorNode, query: string): FindMatch[] {
  const needle = foldForSearch(query.trim())
  if (needle === '') return []
  const found: FindMatch[] = []
  doc.descendants((node, position) => {
    if (!node.isTextblock) return true
    // De tekst van deze alinea, vereenvoudigd, met per teken zijn positie in het document.
    let text = ''
    const positions: number[] = []
    node.forEach((child, offset) => {
      const start = position + 1 + offset
      if (child.isText) {
        const folded = foldForSearch(child.text ?? '')
        for (let index = 0; index < folded.length; index += 1) positions.push(start + index)
        text += folded
      } else {
        positions.push(start)
        text += ATOM
      }
    })
    let at = text.indexOf(needle)
    while (at !== -1) {
      const from = positions[at]
      const last = positions[at + needle.length - 1]
      if (from !== undefined && last !== undefined) found.push({ from, to: last + 1 })
      at = text.indexOf(needle, at + needle.length)
    }
    return false
  })
  return found
}

function decorate(doc: ProseMirrorNode, matches: readonly FindMatch[], active: number): DecorationSet {
  if (matches.length === 0) return DecorationSet.empty
  return DecorationSet.create(
    doc,
    matches.map((match, index) =>
      Decoration.inline(match.from, match.to, {
        class: index === active ? 'ef-zoektreffer ef-zoektreffer--huidig' : 'ef-zoektreffer',
        ...(index === active ? { 'data-huidig': 'ja' } : {}),
      }),
    ),
  )
}

const EMPTY: FindState = { query: '', active: -1, matches: [], decorations: DecorationSet.empty }

function clamp(active: number, count: number): number {
  if (count === 0) return -1
  return ((active % count) + count) % count
}

function next(state: FindState, tr: Transaction): FindState {
  const change = tr.getMeta(FIND_KEY) as FindChange | undefined
  if (change === undefined && !tr.docChanged) return state
  const query = change?.query ?? state.query
  if (query.trim() === '') return EMPTY
  // Opnieuw zoeken bij een andere term en bij elke wijziging in de tekst, ook die van een
  // medeschrijver (R-72): een treffer die is weggetypt hoort niet te blijven staan.
  const matches = change?.query !== undefined || tr.docChanged ? findMatches(tr.doc, query) : state.matches
  const active = clamp(change?.active ?? state.active, matches.length)
  return { query, active, matches, decorations: decorate(tr.doc, matches, active) }
}

function findPlugin(): Plugin<FindState> {
  return new Plugin<FindState>({
    key: FIND_KEY,
    state: {
      init: () => EMPTY,
      apply: (tr, state) => next(state, tr),
    },
    props: {
      decorations: (state) => FIND_KEY.getState(state)?.decorations ?? DecorationSet.empty,
    },
  })
}

/** De plugin als Tiptap-extensie, voor de lijst van één editor. */
export const Find = Extension.create({
  name: 'zoekenInVeld',
  addProseMirrorPlugins: () => [findPlugin()],
})

export function findStateOf(state: EditorState): FindState {
  return FIND_KEY.getState(state) ?? EMPTY
}

/**
 * De eerste treffer op of na deze positie, of de eerste van het veld als er erna geen meer is.
 *
 * Zoeken begint waar de invuller staat, zoals in een tekstverwerker, en niet steeds bovenaan.
 */
export function firstFrom(matches: readonly FindMatch[], position: number): number {
  if (matches.length === 0) return -1
  const index = matches.findIndex((match) => match.from >= position)
  return index === -1 ? 0 : index
}
